<template>
    <DialogOperatorTemplate
        v-model="dialog"
        :target="operator"
        :operators="[operatorName]"
    >
        <!-- Default Modal -->
        <template #default>
            <v-card-subtitle
                v-if="operator.userData?.description"
                class="text-wrap"
            >
                <p>
                    {{
                        operatorType == "dataSources"
                            ? operator.userData.url
                            : operator.userData.description
                    }}
                </p>
            </v-card-subtitle>
        </template>

        <!-- Generic SLOT -->
        <template #[operatorName]="data">
            <!-- This is one of the listed Components imported -->
            <component
                :is="operatorName"
                :data="data"
                :target="target"
                :operatorInputs="operatorInputs"
                @updateFormData="updateFormData"
            >
            </component>
        </template>
        <!-- Actions buttons slot -->
        <template #actions>
            <v-btn color="primary" text @click.stop="close">
                {{ $t("labels.cancel") }}
            </v-btn>
            <v-btn color="secondary" text @click.stop="save" :disabled="!valid">
                {{ $t("labels.save") }}
            </v-btn>
        </template>
    </DialogOperatorTemplate>
    <!-- SNACKBAR -->
    <v-snackbar
        v-model="snackbar"
        :color="snackcolor"
        :timeout="snacktimeout"
        position="absolute"
        :absolute="true"
        :multi-line="true"
        style="white-space: pre-line"
        vertical
    >
        {{ snacktext }}
        <template v-slot:actions>
            <v-btn variant="text" @click="snackbar = false">
                {{ $t("labels.close") }}
            </v-btn>
        </template>
    </v-snackbar>
</template>

<script>
import {
    resolveDynamicComponent,
    inject,
    watch,
    ref,
    computed,
    toRefs,
    provide,
} from "vue";
import { useI18n } from "vue-i18n";
import { preview } from "../mixins/VueFlowEditor.js";
import DialogOperatorTemplate from "./DialogOperatorTemplate.vue";
import OperatorDefault from "./operators/OperatorDefault.vue";
import OperatorInput from "./operators/OperatorInput.vue";
import OperatorCreateObject from "./operators/OperatorCreateObject.vue";
import OperatorSnippet from "./operators/OperatorSnippet.vue";
import OperatorConcatenateStrings from "./operators/OperatorConcatenateStrings.vue";
import OperatorCountElements from "./operators/OperatorCountElements.vue";
import OperatorInputFile from "./operators/OperatorInputFile.vue";
import OperatorIterator from "./operators/OperatorIterator.vue";

export default {
    name: "DialogOperator",
    components: {
        DialogOperatorTemplate,
        OperatorDefault,
        OperatorInput,
        OperatorCreateObject,
        OperatorSnippet,
        OperatorConcatenateStrings,
        OperatorCountElements,
        OperatorInputFile,
        OperatorIterator,
    },
    props: {
        showDialogOperator: Boolean,
        target: Object,
        apiDraw: String,
    },

    emits: ["toggleDialogOperator", "saveFlow"],

    setup(props, { emit }) {
        const { t } = useI18n();

        let snackbar = ref(false);
        let snacktext = ref(t("strings.default-message-snackbar"));
        let snackcolor = ref("green");
        let snacktimeout = ref(2000);
        const setSnackbar = (
            value = snackbar.value,
            text = snacktext.value,
            color = snackcolor.value,
            timeout = snacktimeout.value,
        ) => {
            snackbar.value = value;
            snacktext.value = text;
            snacktext.value = text;
            snackcolor.value = color;
            snacktimeout.value = timeout;
        };
        const valid = ref(false);
        provide("valid", valid);
        const findNodeById = inject("findNodeById");
        const { showDialogOperator, target } = toRefs(props);
        const formData = ref(null);
        const operator = computed(() =>
            JSON.parse(JSON.stringify(target.value)),
        );
        const operatorType = ref(operator.value.type);
        const dialog = computed({
            get: () => showDialogOperator.value,
            set: (value) => emit("toggleDialogOperator", value),
        });

        const operatorName = computed(() => {
            const isComponent = (name) =>
                typeof resolveDynamicComponent(name) !== "string";
            const opName = `Operator${removeSpaces(operator.value?.label)}`;
            return isComponent(opName) ? opName : "OperatorDefault";
        });

        const operatorInputs = ref([]);

        watch(
            () => operator.value.userData?.inputs,
            async (newInputs) => {
                operatorInputs.value = await createInputsArray(newInputs);
            },
            { immediate: true },
        );

        function removeSpaces(str) {
            str = str ? str : "Default";
            return str
                ?.replace(/\s+/g, " ")
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join("")
                .replace(/\//g, "_");
        }

        /**
         * Validates that the provided input is a valid JSON string.
         * Returns true on successful parsing, otherwise shows a warning and returns false.
         */
        function handleInput(input, inputName) {
            if (typeof input !== "string") return false;

            try {
                JSON.parse(input);
                return true;
            } catch {
                setSnackbar(
                    true,
                    t("strings.input-node-invalid", { input_name: inputName }),
                    "warning",
                    -1,
                );
                return false;
            }
        }
        //Close operator dialog and reset formData to null
        function close() {
            dialog.value = false;
        }
        //Save operator settings and close dialog
        function save() {

            const isJson = formData.value.contentType === "json";
            if (isJson) {
                const isValidJson = handleInput(
                    formData.value.defaultValue,
                    formData.value.label,
                );
                if (!isValidJson) {
                    return;
                }
            }
            
            emit("saveFlow", formData.value);
            dialog.value = false;
        }

        //Updates formData with data from operator settings form
        function updateFormData(newFormData) {
            formData.value =
                Object.keys(newFormData).length <= 1
                    ? [...newFormData]
                    : { ...newFormData };
        }

        // Shared helper: builds data payload, calls preview and returns a transformed object
        async function fetchPreviewData(id, inputs) {
            const data = {
                inputs: inputs,
                target: id,
                draw: JSON.parse(sessionStorage.getItem("mashup")),
            };
            const previewData = await preview(props.apiDraw, data);
            return {
                id: id,
                preview: previewData?.result,
                validationErrors: previewData?.validationErrors,
            };
        }

        //Creates an array with the operator inputs objects linked
        async function createInputsArray(ids) {
            let inNodes = []; //Array of inputs
            let opPreviews = []; //Array of operator previews
            if (ids) {
                // 1. Separate in_x ids (input nodes) from op_x ids (operator nodes)
                let inIds = ids?.filter((id) => id.startsWith("in_"));
                let opIds = ids?.filter((id) => id.startsWith("op_"));

                // 2. For each in_x, call the findNodeById function and create an array of nodes

                // Create the inputs object for the preview function
                let inputs = await createInputsObject(inNodes);

                for (let id of inIds) {
                    let node = findNodeById(id);
                    if (node.userData?.idprefix) {
                        inNodes.push(await fetchPreviewData(id, inputs));
                    } else {
                        inNodes.push(node);
                    }
                }

                // 3. For each op_x, call the preview function and create another array with all the results of the called previews
                for (let id of opIds) {
                    opPreviews.push(await fetchPreviewData(id, inputs));
                }
            }
            // 4. In the end, the function returns an array that contains all the node objects and all the preview objects
            return [...inNodes, ...opPreviews];
        }

        /**
         * It takes an array of objects as input.
         * For each object in the array, it adds a new property to the 'objInputs' object.
         * The key of the new property is the 'id' of the current object, and the value is the 'defaultValue' from 'userData'.
         * Finally, it returns the 'objInputs' object.
         */
        async function createInputsObject(arrayIn) {
            let objInputs = {};
            arrayIn.forEach((object) => {
                if (object.userData && object.userData.defaultValue) {
                    objInputs[object.id] = object.userData.defaultValue;
                }
            });
            return objInputs;
        }

        return {
            valid,
            dialog,
            operator,
            operatorType,
            operatorName,
            operatorInputs,
            snackbar,
            snacktext,
            snackcolor,
            snacktimeout,
            setSnackbar,
            close,
            save,
            updateFormData,
        };
    },
};
</script>
